import { useEffect } from "react";
import { useRouteContext } from "@tanstack/react-router";
import { UseFormReturn, useWatch } from "react-hook-form";
import { Container } from "react-bootstrap";

import FormFieldRow from "@/components/shared/forms/FormFieldRow";
import DateInput from "@/components/shared/forms/DateInput";
import ReadOnlyDateInput from "@/components/shared/forms/ReadOnlyDateInput";
import ReadOnlySelect from "@/components/shared/forms/ReadOnlySelect";
import TextInput from "@/components/shared/forms/TextInput";
import Select from "@/components/shared/forms/Select";
import { type PRGM } from "@/models.ts";
import { useCoreData } from "@/queries.ts";
import { usePRGMFormLogic } from "./usePRGMFormLogic";
import { FaBriefcase } from "react-icons/fa";

const HOMELESS_PROGRAM_CODE = "191";

export function PRGMAddEditForm({
  form,
  mode,
}: {
  form: UseFormReturn<PRGM>;
  mode: "view" | "add" | "edit";
}) {
  const leaContext = useRouteContext({ from: "/$leaid" });
  const coreData = useCoreData();
  const tooltipMap = leaContext.coreData.tooltipMap.get("PRGM");
  const viewOnly = mode === "view";
  const locked = mode !== "add";

  const {
    register,
    control,
    trigger,
    getValues,
    formState: { errors },
  } = form;

  const programYear = useWatch({ control, name: "ProgramYear" });
  const eduProgramCode = useWatch({ control, name: "EduProgramCode" });
  const membershipStartDate = useWatch({
    control,
    name: "EduProgramMembershipStartDate",
  });

  const { validateMembershipStart, validateMembershipEnd } = usePRGMFormLogic(
    control,
    viewOnly
  );

  const preschoolOptions = leaContext
    .currentOrgPreschoolsByProgramYear(programYear)
    .map((x) => ({
      Text: x.PreschoolName,
      Value: x.PreschoolCDSCode,
    }));

  const selectedProgramText = coreData.educationalProgramList.find(
    (x) => x.Value === eduProgramCode
  )?.Text;

  useEffect(() => {
    if (viewOnly) return;
    void trigger(["HomelessDwellingTypeCode", "UnaccompaniedYouthIndicator"]);
  }, [eduProgramCode, viewOnly, trigger]);

  useEffect(() => {
    if (viewOnly) return;
    void trigger("EduProgramMembershipEndDate");
  }, [membershipStartDate, viewOnly, trigger]);

  useEffect(() => {
    if (viewOnly) return;
    void trigger([
      "EduProgramMembershipStartDate",
      "EduProgramMembershipEndDate",
    ]);
  }, [programYear, viewOnly, trigger]);

  return (
    <Container
      className="ps-3 pe-3 overview-container"
      style={{ borderTop: "none", borderRadius: 0 }}
    >
      <div className="form-card-modern mb-3">
        <div className="section-header">
          <FaBriefcase className="section-icon" />
          <h5 className="section-title">Education Program Information</h5>
        </div>

        <div className="row g-3">
          <div className="col-md-4">
            <FormFieldRow
              name="EduProgramCode"
              labelText="Education Program"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired={!viewOnly}
            >
              {(id, name) =>
                locked && !viewOnly ? (
                  <ReadOnlySelect
                    id={id}
                    control={control}
                    name={name}
                    rules={{ required: "Education Program is required" }}
                    options={coreData.educationalProgramList}
                  />
                ) : (
                  <Select
                    id={id}
                    disabled={viewOnly}
                    {...register(name, {
                      required: !viewOnly && "Education Program is required",
                    })}
                    options={coreData.educationalProgramList}
                  />
                )
              }
            </FormFieldRow>
          </div>

          <div className="col-md-8">
            <span className="col-form-label invisible d-block" aria-hidden="true">
              Education Program
            </span>
            <p className="text-muted mb-4">
              {selectedProgramText
                ? `This is a placeholder for some text that will give more information about the requirements for the ${selectedProgramText} program.`
                : "Select an Education Program to see more information about its requirements."}
            </p>
          </div>
        </div>

        <div className="row g-3">
          <div className="col-md-4">
            <FormFieldRow
              name="ProgramYear"
              labelText="Program Year"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired={!viewOnly}
            >
              {(id, name) =>
                locked && !viewOnly ? (
                  <ReadOnlySelect
                    id={id}
                    control={control}
                    name={name}
                    rules={{ required: "Program Year is required" }}
                    options={coreData.programYearList}
                  />
                ) : (
                  <Select
                    id={id}
                    disabled={viewOnly}
                    {...register(name, {
                      required: !viewOnly && "Program Year is required",
                    })}
                    options={coreData.programYearList}
                  />
                )
              }
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="PreschoolCode"
              labelText="School"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired={!viewOnly}
            >
              {(id, name) =>
                locked && !viewOnly ? (
                  <ReadOnlySelect
                    id={id}
                    control={control}
                    name={name}
                    rules={{ required: "School is required" }}
                    options={preschoolOptions}
                  />
                ) : (
                  <Select
                    id={id}
                    disabled={viewOnly}
                    {...register(name, {
                      required: !viewOnly && "School is required",
                    })}
                    options={preschoolOptions}
                  />
                )
              }
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="CSPPID"
              labelText="CSPPID"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
            >
              {(id, name) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  readOnly
                  {...register(name)}
                />
              )}
            </FormFieldRow>
          </div>
        </div>

        <div className="row g-3">
          <div className="col-md-4">
            <FormFieldRow
              name="LocalChildId"
              labelText="Local Child ID (CICN)"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
            >
              {(id, name) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  readOnly
                  {...register(name)}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="EduProgramMembershipStartDate"
              labelText="Membership Start Date"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired={!viewOnly}
            >
              {(id, name) =>
                locked && !viewOnly ? (
                  <ReadOnlyDateInput
                    id={id}
                    control={control}
                    name={name}
                    rules={{
                      required: "Membership Start Date is required",
                      validate: validateMembershipStart,
                    }}
                  />
                ) : (
                  <DateInput
                    id={id}
                    disabled={viewOnly}
                    max="9999-12-31"
                    {...register(name, {
                      required:
                        !viewOnly && "Membership Start Date is required",
                      validate: validateMembershipStart,
                    })}
                  />
                )
              }
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="EduProgramMembershipEndDate"
              labelText="Membership End Date"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
            >
              {(id, name) => (
                <DateInput
                  id={id}
                  disabled={viewOnly}
                  max="9999-12-31"
                  {...register(name, {
                    validate: validateMembershipEnd,
                  })}
                />
              )}
            </FormFieldRow>
          </div>
        </div>

        <div className="row g-3">
          <div className="col-md-4">
            <FormFieldRow
              name="HomelessDwellingTypeCode"
              labelText="Homeless Dwelling Type"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired={!viewOnly && eduProgramCode === HOMELESS_PROGRAM_CODE}
            >
              {(id, name) => (
                <Select
                  id={id}
                  disabled={viewOnly}
                  {...register(name, {
                    validate: (v) => {
                      if (viewOnly) return true;
                      if (
                        getValues("EduProgramCode") === HOMELESS_PROGRAM_CODE &&
                        !v
                      ) {
                        return "Homeless Dwelling Type is required when Education Program is Homeless Program (191)";
                      }
                      return true;
                    },
                  })}
                  options={coreData.homelessDwellingTypeCodeList}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="UnaccompaniedYouthIndicator"
              labelText="Unaccompanied Youth Indicator"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired={!viewOnly && eduProgramCode === HOMELESS_PROGRAM_CODE}
            >
              {(id, name) => (
                <Select
                  id={id}
                  disabled={viewOnly}
                  {...register(name, {
                    validate: (v) => {
                      if (viewOnly) return true;
                      if (
                        getValues("EduProgramCode") === HOMELESS_PROGRAM_CODE &&
                        !v
                      ) {
                        return "Unaccompanied Youth Indicator is required when Education Program is Homeless Program (191)";
                      }
                      return true;
                    },
                  })}
                  options={[
                    { Text: "Yes", Value: "Y" },
                    { Text: "No", Value: "N" },
                  ]}
                />
              )}
            </FormFieldRow>
          </div>
        </div>
      </div>
    </Container>
  );
}
